import { Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import TopBar from '@/components/shell/TopBar';
import LeftRail from '@/components/shell/LeftRail';
import ActivityFeed from '@/components/shell/ActivityFeed';
import NotificationStack from '@/components/shell/NotificationStack';
import IncomingCallOverlay from '@/components/shell/IncomingCallOverlay';
import CommandKSearch from '@/components/shell/CommandKSearch';
import FirstVisitModal from '@/components/FirstVisitModal';
import NarrationToast from '@/components/NarrationToast';
import TourGuide from '@/tour/TourGuide';
import { useTourController } from '@/tour/useTourController';
import { useUIStore } from '@/state/uiStore';
import { useSessionStore } from '@/state/sessionStore';
import { useControlBridge } from '@/hooks/useControlBridge';

export default function AppShell() {
  const navigate = useNavigate();
  const activityFeedOpen = useUIStore((s) => s.activityFeedOpen);
  const setModal = useUIStore((s) => s.setModal);
  const persona = useSessionStore((s) => s.persona);
  const [cmdKOpen, setCmdKOpen] = useState(false);
  useControlBridge();
  useTourController();

  // Global keyboard shortcuts. The old chronological control-panel slide-down
  // has been replaced by the /playtest demo console — Cmd+. now opens it.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return;

      if (meta && e.key === '.') {
        e.preventDefault();
        navigate('/playtest');
        return;
      }
      if (meta && e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        setModal('persona-switcher', true);
        navigate('/');
        return;
      }
      if (meta && e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        setModal('studio-switcher', true);
        return;
      }
      if (meta && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdKOpen((v) => !v);
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate, setModal]);

  if (!persona) {
    return (
      <div className="bg-ink-black text-cream p-6">
        Persona not selected. Returning to splash.
      </div>
    );
  }

  return (
    <div className="app-shell-root h-screen w-full bg-ink-black text-cream flex flex-col overflow-hidden">
      <TopBar />
      <div className="flex flex-1 min-h-0">
        <LeftRail />
        <main className="flex-1 min-w-0 overflow-y-auto">
          <Outlet />
        </main>
        {activityFeedOpen ? <ActivityFeed /> : null}
      </div>
      <NotificationStack />
      <IncomingCallOverlay />
      <CommandKSearch open={cmdKOpen} onClose={() => setCmdKOpen(false)} />
      <FirstVisitModal />
      <NarrationToast />
      <TourGuide />
    </div>
  );
}
